import React, {useEffect, useState} from 'react';
import {Box, CircularProgress, Stack, styled, TextField} from '@mui/material';
import {
  Body,
  Button,
  designSystemToken,
  Icon,
} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import {ErrorKind} from '@contentlab/stores/contentLabStore';
import {raiseContentLabError} from '@contentlab/utils/contentLabErrorHandling';
import {SrtItem} from '@contentlab/utils/srtUtils';
import {
  CONTENT_LAB_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import projectFetchers from '@/api/fetchers/content-studio/project';
import {calculateLength, calculatePhraseScore} from '../util';

const Container = styled(Stack)(() => ({
  backgroundColor: designSystemToken('semantic.bg.alpha'),
  borderRadius: '8px',
  padding: '8px',
  gap: '10px',
}));

const Header = styled(Stack)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0 8px 0 4px',
}));

const SrtTextField = styled(TextField)(() => ({
  'backgroundColor': designSystemToken('semantic.bg.secondary'),
  'borderRadius': '8px',
  '& .MuiInputBase-input': {
    fontSize: '18px',
    color: designSystemToken('semantic.fg.primary'),
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      border: `1px solid ${designSystemToken('semantic.bg.neutral-inverse')}`,
    },
    '&:hover fieldset': {
      border: `1px solid ${designSystemToken('semantic.bg.neutral-inverse')}`,
    },
    '&.Mui-focused fieldset': {
      border: `1px solid ${designSystemToken('semantic.bg.neutral-inverse')}`,
    },
  },
}));

const Footer = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
}));

function SrtRowEditor({
  testID = 'srt-row-editor',
  projectId,
  item,
  editRow,
  isEdited,
  restoreOriginal,
  currentIndex,
  setCurrentIndex,
}: {
  testID?: string;
  projectId: string;
  item: SrtItem;
  editRow: (newText: string, shouldCloseRow: boolean) => void;
  isEdited: boolean;
  restoreOriginal: () => void;
  currentIndex: string | null;
  setCurrentIndex: (index: string | null) => void;
}) {
  const {uiInteraction} = useUiInteractionEvent();
  const [newText, setNewText] = useState(item.currentText);
  const {phrase, color} = calculatePhraseScore(
    calculateLength(newText) - calculateLength(item.originalText)
  );
  const [tts, setTts] = useState<Blob | null>(null);
  useEffect(() => {
    if (tts) {
      const audio = new Audio(URL.createObjectURL(tts));
      audio.play();
    }
  }, [tts]);
  const [isLoadingSound, setIsLoadingSound] = useState(false);

  const onSubmit = (shouldCloseRow = true) => {
    uiInteraction({
      item_name: 'rephrase_row_editor_update_script',
      interaction_location: CONTENT_LAB_VIEW_NAMES.REPHRASE_ROW_EDITOR,
      interaction_details: newText,
      flow_name: FLOW_NAMES.CONTENT_LAB.REPHRASE,
    });
    editRow(newText, shouldCloseRow);
  };

  return (
    <Container data-testid={testID}>
      <Header>
        <Body
          size="md"
          color={designSystemToken('semantic.fg.inverse-secondary')}
          dangerouslySetInnerHTML={{
            __html: translate('original_length_and_time', {
              originalLength: calculateLength(item.originalText),
              originalTime: (item.endSeconds - item.startSeconds).toFixed(2),
            }),
          }}
        />
        <Button
          appearance="neutral"
          mode="tinted"
          size="small"
          isLoading={isLoadingSound}
          disabled={isLoadingSound}
          icon={
            <Icon name="Features-Audio-On" size="medium" appearance="primary" />
          }
          onClick={() => {
            uiInteraction({
              item_name: 'rephrase_row_editor_play_audio',
              interaction_location: CONTENT_LAB_VIEW_NAMES.REPHRASE_ROW_EDITOR,
              interaction_details: newText,
              flow_name: FLOW_NAMES.CONTENT_LAB.REPHRASE,
            });
            if (projectId && !isLoadingSound) {
              setIsLoadingSound(true);
              projectFetchers
                .getTTSByProjectId(projectId, newText)
                .then((response) => {
                  setTts(response);
                })
                .catch(() => {
                  raiseContentLabError({
                    errorMessage: 'Failed to get TTS',
                    errorKind: ErrorKind.FAILURE,
                  });
                })
                .finally(() => {
                  setIsLoadingSound(false);
                });
            }
          }}
        />
      </Header>
      <SrtTextField
        multiline
        maxRows={4}
        autoFocus
        value={newText}
        onChange={(e) => {
          setNewText(e.target.value);
          editRow(e.target.value, false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSubmit(false);
            if (currentIndex !== null) {
              setCurrentIndex(String(Number.parseInt(currentIndex, 10) + 1));
            }
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onSubmit(true);
          }
        }}
        inputProps={{
          spellCheck: false,
          autoCapitalize: 'off',
          autoCorrect: 'off',
        }}
      />
      <Footer>
        {isEdited && (
          <>
            <Body
              size="md"
              color={designSystemToken('semantic.fg.inverse-secondary')}
              dangerouslySetInnerHTML={{
                __html: translate('new_length', {
                  newLength: calculateLength(newText),
                }),
              }}
            />
            <Body size="md" color={color}>
              &nbsp;{phrase}
            </Body>
          </>
        )}
        <Button
          appearance="neutral"
          mode="plain"
          size="small"
          disabled={!isEdited}
          sx={{
            marginLeft: 'auto',
          }}
          onClick={() => {
            restoreOriginal();
            setNewText(item.originalText);
            uiInteraction({
              item_name: 'rephrase_row_editor_restore_original',
              interaction_location: CONTENT_LAB_VIEW_NAMES.REPHRASE_ROW_EDITOR,
              interaction_details: item.originalText,
              flow_name: FLOW_NAMES.CONTENT_LAB.REPHRASE,
            });
          }}
        >
          {translate('Restore original')}
        </Button>
        <Button
          appearance="neutral"
          mode="tinted"
          disabled={!isEdited}
          size="small"
          onClick={() => onSubmit(true)}
        >
          {translate('Done')}
        </Button>
      </Footer>
    </Container>
  );
}
export default SrtRowEditor;
